import React, { useEffect, useMemo, useState } from "react";

import { useParams, useNavigate } from "react-router-dom";

import { getFormByQR } from "./formApi";

import { CATEGORIES, categoryOf, termsFor } from "./FormCategories";



/*
====================================================================
FormPortal.jsx

The screen a patient lands on after scanning the QR.

    Welcome                                    [New patient? Register]
      ✦  Aesthetic          →   Skin
      ☯  Energy & Wellness      Hair            →  the forms
                                Skin & Hair


STYLES ARE EMBEDDED, NOT IMPORTED
---------------------------------
There is no `import "./FormPortal.css"`. Everything is in the `css`
constant at the foot of this file, injected with a <style> tag — the
same pattern QrGenerator.jsx uses.

Why: the page kept rendering as a flat stack because an older
FormPortal.css was still winning — it styled .fp-tile as display:block
and pinned the buttons with position:fixed. Same specificity means
whichever rule loads last wins, which is not something this component
can control from the outside.

Keeping the styles here means the layout has exactly one source and
cannot be half-overridden. Every selector is scoped under .fp-page, so
nothing leaks into DynamicForm or any other screen. FormPortal.css can
be deleted once nothing else imports it.


RESPONSIVE
----------
Mobile first. One column up to 600px; the top-level categories go two
across above that. Type and spacing scale with clamp() rather than
stepping at breakpoints, so there is no width where it looks wrong.
Tap targets stay at least 44px. Also handles landscape phones, which
are short rather than narrow, and honours prefers-reduced-motion.


HOW A FORM FINDS ITS CATEGORY
-----------------------------
`forms` has no category column, so it is matched on form_name +
form_type by the ordered rules below. FIRST MATCH WINS, and the order
matters twice:

  - "Yoga Registration & Consent Form" contains "registration", so the
    yoga rule is tested before the registration rule, or every themed
    form would end up behind the Registration button.

  - "Additional Clinic Forms" is matched to HAIR: its content is the
    Hamilton-Norwood chart, a male pattern baldness scale, plus the
    session tracker and photography consent that go with it.

To place a new form deliberately, put the category's keyword in its
name or form_type. If a `category` column is added later, categoryOf()
reads it first and these rules become the fallback.
====================================================================
*/


/*
  Categories, the matching rules and the per-category wording all live
  in formCategories.js — SingleFormPage needs the same answers and this
  screen must not be the only place that knows them.
*/
/*
  Whether forms that match no category appear on the Welcome screen
  under an "Other forms" heading.

  false: they are hidden. That is right for this clinic — the only
  unplaced form is "Additional Clinic Forms", and 16 of its 18 fields
  are doctorOnly, so a patient opening it sees one photography-consent
  question under a heading promising much more.

  BE DELIBERATE ABOUT THIS. A form hidden here is unreachable from the
  QR code entirely; a patient cannot ask for a form they never see. It
  is still reachable by direct link, which is how a clinician opens it:

      /forms/<token>/<admin_id>/<form_id>?scope=doctor

  Set to true if a new form ever needs to be patient-reachable before
  it has a category.
*/
const SHOW_UNCATEGORISED = false;

const norm = (s) => String(s || "").toLowerCase();


const FormPortal = () => {

    const { token } = useParams();
    const navigate = useNavigate();

    const [formsData, setFormsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    /*
      One object decides what is on screen, so Back always has a
      defined destination.

        { level: "root" }
        { level: "parent",       parentId }
        { level: "group",        parentId, groupId }
        { level: "registration" }
    */
    const [view, setView] = useState({ level: "root" });

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const res = await getFormByQR(token);
                if (!cancelled) setFormsData(res.data);
            } catch (err) {
                console.log(err);
                if (!cancelled) {
                    setError(err.response?.data?.message || err.message || "Unknown error");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [token]);

    // Scroll to top when moving between levels — on a phone, tapping a
    // tile near the bottom otherwise lands you mid-page on the next one.
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [view]);

    const forms = useMemo(() => formsData?.forms || [], [formsData]);

    /*
      A form can sit in more than one category — categoryOf may return
      an array. Nothing does yet, but the shared clinic documents are
      the obvious case: a Treatment Plan belongs equally to Skin and to
      Hair, and duplicating the row to file it twice would be worse.
    */
    const grouped = useMemo(() => {
        const map = {};
        forms.forEach((f) => {
            const ids = categoryOf(f);
            (Array.isArray(ids) ? ids : [ids]).forEach((id) => {
                (map[id] = map[id] || []).push(f);
            });
        });
        return map;
    }, [forms]);

    const listOf = (id) => grouped[id] || [];

    const registrationForms = listOf("registration");

    /*
      Anything that did not land in a known category.

      Previously this was listOf("other") — an exact match — so a form
      whose category was "additional", "consultation" or any other id
      not in CATEGORIES went into a bucket that nothing rendered, and
      simply disappeared from the portal. A patient cannot report a form
      they cannot see, so this now sweeps up EVERY unplaced form rather
      than the one id we happened to expect.
    */
    const otherForms = useMemo(() => {
        if (!SHOW_UNCATEGORISED) return [];

        const placed = new Set(
            CATEGORIES.flatMap((c) => c.children.map((ch) => ch.id))
                .concat("registration")
        );

        return Object.entries(grouped)
            .filter(([id]) => !placed.has(id))
            .flatMap(([, list]) => list)
            // a form in two unplaced categories would otherwise list twice
            .filter((f, i, arr) => arr.findIndex((x) => x.id === f.id) === i);
    }, [grouped]);

    const parentCount = (parent) =>
        parent.children.reduce((n, c) => n + listOf(c.id).length, 0);

    const parentOf = (id) => CATEGORIES.find((c) => c.id === id);
    const childOf = (parentId, groupId) =>
        parentOf(parentId)?.children.find((c) => c.id === groupId);

    const openForm = (form) =>
        navigate(`/forms/${token}/${formsData.admin_id}/${form.id}`);

    const openRegistration = () => {
        // One registration form is the normal case — don't show a list of one.
        if (registrationForms.length === 1) return openForm(registrationForms[0]);
        setView({ level: "registration" });
    };

    const back = () => {
        if (view.level === "group") {
            setView({ level: "parent", parentId: view.parentId });
            return;
        }
        setView({ level: "root" });
    };


    /* --------------------------- pieces ---------------------------- */

    const Shell = ({ title, subtitle, crumb, showRegistration, wide, children }) => (
        <div className="fp-page">
            <style>{css}</style>

            <div className="fp-shell">

                <div className="fp-topbar">
                    {crumb ? (
                        <button className="fp-back" type="button" onClick={back}>
                            <span aria-hidden="true">←</span> Back
                        </button>
                    ) : <span className="fp-spacer" />}

                    {showRegistration && registrationForms.length > 0 && (
                        <button
                            className="fp-register"
                            type="button"
                            onClick={openRegistration}
                        >
                            <span aria-hidden="true">＋</span> New Client? Register
                        </button>
                    )}
                </div>

                <div className="fp-card">
                    <div className="fp-header">
                        {crumb && <div className="fp-crumb">{crumb}</div>}
                        <h2>{title}</h2>
                        {subtitle && <p>{subtitle}</p>}
                    </div>

                    <div className={`fp-body${wide ? " fp-body-grid" : ""}`}>
                        {children}
                    </div>
                </div>

                <p className="fp-foot">
                    Not sure which form you need? Please ask at the reception desk.
                </p>

            </div>
        </div>
    );

    /*
      count === null/undefined -> no badge (a form tile: end of the road)
      count === 0              -> dimmed, "Soon"
    */
    const Tile = ({ icon, label, blurb, count, onClick }) => {
        const empty = count === 0;

        return (
            <button
                type="button"
                className={`fp-tile${empty ? " is-empty" : ""}`}
                onClick={onClick}
            >
                {icon && <span className="fp-tile-icon" aria-hidden="true">{icon}</span>}

                <span className="fp-tile-text">
                    <span className="fp-tile-label">{label}</span>
                    {blurb && <span className="fp-tile-blurb">{blurb}</span>}
                </span>

                <span className="fp-tile-right">
                    {count === null || count === undefined ? null : (
                        <span className={`fp-count${empty ? " is-zero" : ""}`}>
                            {empty ? "Soon" : count}
                        </span>
                    )}
                    <span className="fp-chevron" aria-hidden="true">›</span>
                </span>
            </button>
        );
    };

    const formTiles = (list) =>
        list.map((form) => (
            <Tile
                key={form.id}
                label={form.form_name}
                blurb={form.description}
                onClick={() => openForm(form)}
            />
        ));

    const EmptyState = ({ what }) => (
        <div className="fp-empty">
            <div className="fp-empty-icon" aria-hidden="true">◌</div>
            <p className="fp-empty-title">No {what} forms yet</p>
            <p className="fp-empty-text">
                This category hasn’t been set up at this clinic. Please choose
                another, or ask at the reception desk.
            </p>
        </div>
    );

    const Loading = () => (
        <div className="fp-page">
            <style>{css}</style>

            <div className="fp-shell">
                <div className="fp-topbar"><span className="fp-spacer" /></div>
                <div className="fp-card">
                    <div className="fp-header">
                        <div className="sk sk-title" />
                        <div className="sk sk-sub" />
                    </div>
                    <div className="fp-body">
                        {[0, 1, 2].map((i) => (
                            <div key={i} className="fp-tile is-skeleton">
                                <span className="sk sk-icon" />
                                <span className="fp-tile-text">
                                    <span className="sk sk-label" />
                                    <span className="sk sk-blurb" />
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );


    /* -------------------------- states ----------------------------- */

    if (loading) return <Loading />;

    if (error) {
        return (
            <Shell title="Something went wrong">
                <div className="fp-empty">
                    <div className="fp-empty-icon" aria-hidden="true">!</div>
                    <p className="fp-empty-text">{error}</p>
                </div>
            </Shell>
        );
    }

    if (!forms.length) {
        return (
            <Shell title="No forms available">
                <EmptyState what="" />
            </Shell>
        );
    }

    if (view.level === "registration") {
        return (
            <Shell
                title="Registration"
                subtitle="Choose the form you have been asked to fill in"
                crumb="Welcome"
            >
                {formTiles(registrationForms)}
            </Shell>
        );
    }

    if (view.level === "group") {
        const parent = parentOf(view.parentId);
        const child = childOf(view.parentId, view.groupId);
        const list = listOf(view.groupId);

        const t = termsFor(view.groupId);

        return (
            <Shell
                title={child?.label || "Forms"}
                subtitle={list.length ? `Choose a ${t.person} form to fill in` : null}
                crumb={`Welcome › ${parent?.label || ""}`}
            >
                {list.length
                    ? formTiles(list)
                    : <EmptyState what={norm(child?.label || "")} />}
            </Shell>
        );
    }

    if (view.level === "parent") {
        const parent = parentOf(view.parentId);

        /*
          Aesthetic says "patient", Energy & Wellness says "client".
          termsFor takes a category id, so any child of this branch
          gives the same answer — see formCategories.js.
        */
        const t = termsFor(parent?.children[0]?.id);

        return (
            <Shell
                title={parent?.label}
                subtitle={`Which ${t.person} form do you need?`}
                crumb="Welcome"
            >
                {parent?.children.map((c) => {
                    const list = listOf(c.id);

                    return (
                        <Tile
                            key={c.id}
                            label={c.label}
                            blurb={c.blurb}
                            count={list.length}
                            onClick={() =>
                                /*
                                  One form in this category — open it. A
                                  list of one is a screen that exists only
                                  to be tapped through, and on a phone in a
                                  waiting room that is a tap wasted.
                                */
                                list.length === 1
                                    ? openForm(list[0])
                                    : setView({
                                        level: "group",
                                        parentId: parent.id,
                                        groupId: c.id
                                      })
                            }
                        />
                    );
                })}
            </Shell>
        );
    }

    /* root */
    return (
        <Shell
            title="Welcome"
            subtitle="What is your visit about today?"
            showRegistration
            wide
        >
            {CATEGORIES.map((p) => (
                <Tile
                    key={p.id}
                    icon={p.icon}
                    label={p.label}
                    blurb={p.blurb}
                    count={parentCount(p)}
                    onClick={() => setView({ level: "parent", parentId: p.id })}
                />
            ))}

            {/* Nothing is hidden because it matched no rule. */}
            {otherForms.length > 0 && (
                <>
                    <div className="fp-divider"><span>Other forms</span></div>
                    {formTiles(otherForms)}
                </>
            )}
        </Shell>
    );
};


/* ==================================================================
   STYLES — every selector scoped under .fp-page
   ================================================================== */

const css = `
.fp-page {
  --teal: #172056;
  --teal-dark: #0F1640;
  --teal-tint: #F1F3F8;
  --ink: #14172E;
  --muted: #71858a;
  --line: #CDD3E5;
  --bg: #F1F2F7;
  --radius: 16px;

  box-sizing: border-box;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: clamp(12px, 3.5vw, 48px) clamp(12px, 4vw, 24px)
           clamp(32px, 6vw, 64px);
  background: var(--bg);
  font-family: "Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  color: var(--ink);
  -webkit-font-smoothing: antialiased;
}

.fp-page *,
.fp-page *::before,
.fp-page *::after { box-sizing: border-box; }

.fp-shell { width: 100%; max-width: 620px; }

/* ---------------------------- top bar ---------------------------- */
/* In the flow, not fixed: on a landscape phone a fixed bar sits on
   top of the card. space-between holds the two ends apart even when
   only one button is present. */
.fp-topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  margin-bottom: clamp(10px, 2vw, 16px);
}

.fp-spacer { display: block; }

.fp-back,
.fp-register {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 44px;
  padding: 10px 18px;
  border-radius: 999px;
  font: inherit;
  font-size: clamp(13px, 3.4vw, 14px);
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: background .16s ease, border-color .16s ease, transform .1s ease;
}

.fp-back {
  background: #fff;
  color: var(--teal);
  border: 1px solid var(--line);
}
.fp-back:hover { border-color: var(--teal); }

.fp-register {
  background: var(--teal);
  color: #fff;
  border: 1px solid var(--teal);
  box-shadow: 0 2px 10px rgba(23,32,86,.28);
}
.fp-register:hover { background: var(--teal-dark); }

.fp-back:active,
.fp-register:active { transform: translateY(1px); }

.fp-back:focus-visible,
.fp-register:focus-visible {
  outline: 2px solid var(--teal);
  outline-offset: 2px;
}

/* ----------------------------- card ------------------------------ */
.fp-card {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(20,23,46,.04), 0 14px 34px rgba(20,23,46,.08);
}

.fp-header {
  background: linear-gradient(135deg, var(--teal) 0%, var(--teal-dark) 100%);
  color: #fff;
  padding: clamp(18px, 4.5vw, 30px) clamp(16px, 4.5vw, 30px);
}

.fp-header h2 {
  margin: 0;
  font-size: clamp(19px, 5.2vw, 26px);
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -.01em;
  color: #fff;
  text-transform: capitalize;
}

.fp-header p {
  margin: 6px 0 0;
  font-size: clamp(13px, 3.5vw, 14.5px);
  line-height: 1.5;
  color: rgba(255,255,255,.86);
}

.fp-crumb {
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: .09em;
  text-transform: uppercase;
  color: rgba(255,255,255,.72);
  margin-bottom: 7px;
}

.fp-body { padding: clamp(14px, 4vw, 22px); }

/* ----------------------------- tiles ----------------------------- */
.fp-tile {
  display: flex;
  align-items: center;
  gap: clamp(11px, 3vw, 15px);
  width: 100%;
  min-height: 68px;
  text-align: left;
  padding: clamp(13px, 3.5vw, 17px) clamp(14px, 3.5vw, 18px);
  margin-bottom: 11px;
  border: 1px solid var(--line);
  border-radius: 13px;
  background: #fff;
  font: inherit;
  color: inherit;
  cursor: pointer;
  transition: border-color .16s ease, background .16s ease,
              box-shadow .16s ease, transform .1s ease;
}

.fp-tile:last-child { margin-bottom: 0; }

.fp-tile:hover {
  border-color: var(--teal);
  background: var(--teal-tint);
  box-shadow: 0 3px 14px rgba(23,32,86,.12);
}

.fp-tile:active { transform: translateY(1px); }

.fp-tile:focus-visible {
  outline: 2px solid var(--teal);
  outline-offset: 2px;
}

.fp-tile-icon {
  flex: 0 0 auto;
  width: clamp(38px, 11vw, 46px);
  height: clamp(38px, 11vw, 46px);
  display: grid;
  place-items: center;
  border-radius: 12px;
  background: var(--teal-tint);
  color: var(--teal);
  font-size: clamp(17px, 5vw, 21px);
  line-height: 1;
}

.fp-tile-text { flex: 1 1 auto; min-width: 0; }

.fp-tile-label {
  display: block;
  font-size: clamp(14.5px, 4vw, 16px);
  font-weight: 700;
  line-height: 1.35;
  color: var(--ink);
  /* form_name is stored lowercase in places — see
     "patient registration consultation form" */
  text-transform: capitalize;
  overflow-wrap: anywhere;
}

.fp-tile-blurb {
  display: block;
  margin-top: 3px;
  font-size: clamp(12px, 3.3vw, 13px);
  line-height: 1.45;
  color: var(--muted);
  text-transform: none;
}

.fp-tile-right {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 9px;
}

.fp-count {
  min-width: 25px;
  height: 23px;
  padding: 0 9px;
  display: inline-grid;
  place-items: center;
  border-radius: 999px;
  background: var(--teal-tint);
  color: var(--teal);
  font-size: 12px;
  font-weight: 700;
}

.fp-count.is-zero {
  background: #f1f4f4;
  color: #97a4a7;
  font-weight: 600;
}

.fp-chevron {
  color: #b9c7ca;
  font-size: 22px;
  line-height: 1;
}
.fp-tile:hover .fp-chevron { color: var(--teal); }

/* A category with nothing in it yet — still shown, so the menu doesn't
   change shape as forms are added, but visibly not ready. */
.fp-tile.is-empty { background: #fbfcfc; }
.fp-tile.is-empty .fp-tile-label { color: #67787c; }
.fp-tile.is-empty .fp-tile-icon { background: #f1f4f4; color: #97a4a7; }

/* --------------------------- divider ----------------------------- */
.fp-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 20px 0 13px;
  color: var(--muted);
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: .09em;
  text-transform: uppercase;
}
.fp-divider::before,
.fp-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: var(--line);
}

/* ------------------------- empty state --------------------------- */
.fp-empty { text-align: center; padding: clamp(24px, 7vw, 40px) 16px; }

.fp-empty-icon {
  width: 54px; height: 54px;
  margin: 0 auto 12px;
  display: grid; place-items: center;
  border-radius: 50%;
  background: var(--teal-tint);
  color: #B3BCD3;
  font-size: 26px;
  line-height: 1;
}

.fp-empty-title {
  margin: 0 0 6px;
  font-size: 16px;
  font-weight: 700;
  color: var(--ink);
  text-transform: capitalize;
}

.fp-empty-text {
  margin: 0 auto;
  max-width: 38ch;
  font-size: 13.5px;
  line-height: 1.6;
  color: var(--muted);
}

/* ---------------------------- footer ----------------------------- */
.fp-foot {
  margin: clamp(14px, 3vw, 20px) 0 0;
  text-align: center;
  font-size: clamp(12.5px, 3.3vw, 13.5px);
  line-height: 1.55;
  color: var(--muted);
}

/* --------------------------- skeleton ---------------------------- */
.fp-tile.is-skeleton { cursor: default; pointer-events: none; }

.fp-page .sk {
  position: relative;
  overflow: hidden;
  display: block;
  background: #e6eef0;
  border-radius: 7px;
}
.fp-page .sk::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.7), transparent);
  animation: fpShimmer 1.4s infinite;
}
.fp-page .sk-title { width: 45%; height: 24px; background: rgba(255,255,255,.3); }
.fp-page .sk-sub   { width: 62%; height: 13px; margin-top: 10px; background: rgba(255,255,255,.22); }
.fp-page .sk-icon  { width: 44px; height: 44px; border-radius: 12px; flex: 0 0 auto; }
.fp-page .sk-label { width: 52%; height: 14px; }
.fp-page .sk-blurb { width: 74%; height: 11px; margin-top: 8px; }

@keyframes fpShimmer { 100% { transform: translateX(100%); } }

/* ========================= RESPONSIVE ============================ */

/* Very small phones (320–359px): drop the icon so the label keeps
   room, and let a long form name wrap without squeezing the chevron. */
@media (max-width: 359px) {
  .fp-tile { gap: 10px; padding: 12px; }
  .fp-tile-icon { display: none; }
  .fp-count { display: none; }
}

/* Landscape phones — short, not narrow. Trim vertical space so the
   first tiles stay above the fold. */
@media (max-height: 480px) and (orientation: landscape) {
  .fp-page { padding-top: 10px; padding-bottom: 20px; }
  .fp-header { padding-top: 14px; padding-bottom: 14px; }
  .fp-tile { min-height: 58px; margin-bottom: 9px; }
  .fp-foot { display: none; }
}

/* Tablets and up: the two top-level categories side by side. Only the
   root screen opts in (fp-body-grid), because sub-menus and form lists
   read better as a single scannable column. */
@media (min-width: 600px) {
  .fp-body-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }

  .fp-body-grid .fp-tile {
    flex-direction: column;
    align-items: flex-start;
    min-height: 150px;
    margin-bottom: 0;
    padding: 20px;
  }

  .fp-body-grid .fp-tile-icon { margin-bottom: 12px; }

  .fp-body-grid .fp-tile-right {
    position: absolute;
    top: 18px;
    right: 18px;
  }

  .fp-body-grid .fp-tile { position: relative; }

  .fp-body-grid .fp-chevron { display: none; }

  /* The divider and any "other" form tiles run the full width under
     the two category cards. */
  .fp-body-grid .fp-divider,
  .fp-body-grid .fp-tile-form { grid-column: 1 / -1; }
  .fp-body-grid .fp-divider { margin: 6px 0 0; }
}

/* Desktop: a touch more air, still a single readable column width. */
@media (min-width: 1024px) {
  .fp-shell { max-width: 660px; }
  .fp-tile:hover { transform: translateY(-1px); }
  .fp-body-grid .fp-tile:hover { transform: translateY(-2px); }
}

@media (prefers-reduced-motion: reduce) {
  .fp-page .sk::after { animation: none; }
  .fp-tile, .fp-back, .fp-register { transition: none; }
  .fp-tile:hover, .fp-body-grid .fp-tile:hover { transform: none; }
}
`;

export default FormPortal;